import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Phone,
    Video,
    MessageSquareText,
    ShieldCheck,
    Zap,
    Gavel,
    Ruler,
    Landmark,
    Building2,
} from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";

const IconFromName = ({ name, className = "w-5 h-5" }) => {
    switch (name) {
        case "gavel":
            return <Gavel className={className} />;
        case "ruler":
            return <Ruler className={className} />;
        case "landmark":
            return <Landmark className={className} />;
        case "building":
            return <Building2 className={className} />;
        default:
            return <Gavel className={className} />;
    }
};

const QuickIcon = ({ name, className = "w-5 h-5" }) => {
    if (name === "chat") return <MessageSquareText className={className} />;
    if (name === "call") return <Phone className={className} />;
    return <Video className={className} />;
};

export function TeletorgetHero({
    imageUrl =
    "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=1600&auto=format&fit=crop",
    title = "Advokatene",
    body =
    "Rask tilgang til erfarne advokater – chat, telefon eller video. Bestill konsultasjon på minutter.",
    rateText = "66 kr/min",
    ctas = [
        { label: "Book konsultasjon", href: "#agents", kind: "primary" },
        { label: "Registrer deg", href: "#signup", kind: "secondary" },
    ],
    onRegisterClick,
    baseUrl,
    handleChipClick,
}) {
    const client = useSelector((state) => state.client.client);
    const default_configs = client ? JSON.parse(client.default_configs) : {};
    const footer_settings = client ? JSON.parse(client.footer_settings) : {};
    const quick = [
        { label: "Chat", href: "#chat", icon: "chat" },
        { label: "Ring", href: "#", icon: "call" },
        { label: "Video", href: "#agents", icon: "video" },
    ];

    // const chips = [
    //     { icon: "gavel", label: "Advokat", href: "#advokat" },
    //     { icon: "ruler", label: "Ingeniør", href: "#ingenior" },
    //     { icon: "building", label: "Eiendom", href: "#eiendom" },
    //     { icon: "landmark", label: "Arkitekt", href: "#arkitekt" },
    // ];
    const [chips, setChips] = useState([]);
    useEffect(() => {
        axios.get(baseUrl + '/getSkills', { params: { client_uid: client.uid } })
            .then((response) => {
                if (response.data.status === "OK") {
                    setChips(response.data.data);
                }
            })
    }, [client])
    const onRingClick = () => {
        if (default_configs.WebRTC !== "yes") {
            window.location.href = "tel:" + footer_settings.footer_phone;
        } else {
            document.getElementById("agents")?.scrollIntoView({ behavior: "smooth" });
        }
    }
    return (
        <div className="w-full max-w-full mx-auto">
            <div className="relative w-full overflow-hidden shadow-xl h-screen">
                <img
                    src={imageUrl}
                    alt="Bakgrunn – advokatkontor"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

                <div className="relative z-10 flex items-center justify-start h-full py-8 md:py-0">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-white max-w-lg sm:max-w-xl md:max-w-2xl px-4 sm:px-6 md:px-12"
                    >
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight drop-shadow">
                            {title}
                        </h1>
                        <p className="mt-4 text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                            {body}
                        </p>

                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {chips.map((c, i) => (
                                <a
                                    key={i}
                                    href={c.href || "#"}
                                    onClick={() => handleChipClick(c.name)}
                                    className="flex items-center gap-2 rounded-xl bg-white/95 text-gray-900 px-4 py-3 text-sm sm:text-base hover:bg-white focus:ring-2 focus:ring-orange-500 transition"
                                >
                                    <img alt={c.name} src={c.picture} className="w-4 h-4 sm:w-5 sm:h-5 opacity-80 object-cover" />
                                    <span className="font-medium">{c.name}</span>
                                </a>
                            ))}
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            {ctas.map((c, i) => (
                                <a
                                    key={i}
                                    href={c.href}
                                    onClick={(e) => {
                                        if (c.kind === "secondary") {
                                            e.preventDefault(); // stop page jump
                                            onRegisterClick && onRegisterClick();
                                        }
                                    }}
                                    className={[
                                        "inline-flex items-center justify-center px-5 py-3 rounded-xl font-semibold transition text-sm sm:text-base",
                                        c.kind === "primary"
                                            ? "bg-orange-500 text-white hover:bg-orange-600 focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                            : "bg-white/95 text-gray-900 hover:bg-white focus:ring-2 focus:ring-offset-2 focus:ring-gray-200",
                                    ].join(" ")}
                                >
                                    {c.label}
                                </a>
                            ))}
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-4 text-white/90 text-sm sm:text-base">
                            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" /> Sikker ID & betaling</div>
                            <div className="flex items-center gap-2"><Zap className="w-4 h-4 sm:w-5 sm:h-5" /> Raskt oppsett</div>

                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-3 sm:gap-4">
                            {quick.map((q, i) => (
                                <a
                                    key={i}
                                    href={q.href}
                                    aria-label={q.label}
                                    onClick={(e) => {
                                        if (q.icon === "call") {
                                            e.preventDefault();
                                            onRingClick && onRingClick();
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full bg-white/95 text-gray-900 px-3 sm:px-4 py-2 text-sm sm:text-base hover:bg-white focus:ring-2 focus:ring-orange-500 transition"
                                >
                                    <QuickIcon name={q.icon} />
                                    <span className="font-medium">{q.label}</span>
                                </a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export function Hero({ hero, onRegisterClick, baseUrl, handleChipClick }) {
    return (
        <div className="min-h-screen bg-neutral-100">
            <TeletorgetHero onRegisterClick={onRegisterClick} baseUrl={baseUrl} handleChipClick={handleChipClick} />
        </div>
    );
}